import AttemptAnalysisLegend from '../../components/analysis/AttemptAnalysisLegend';
import { useEffect, useState } from 'react';
import { cachedGet } from '../../lib/requestDedupe';
import AttemptAnalysisSummaryTable from './tables/AttemptAnalysisSummaryTable';
import SubjectAttemptAnalysisChart from './charts/SubjectAttemptAnalysisChart';
import TableHeader from '../../components/generic/TableHeader';
import { Tab, TabsContainer } from '../../utils/SubjectTabs';
import ComparativeAttemptAnalysisTable from './tables/ComparativeAttemptAnalysisTable';
import ComparativeAttemptAnalysisChart from './charts/ComparativeAttemptAnalysisChart';
import TestAnalysisLoader from '../../components/loaders/TestAnalysisLoader';

export default function AttemptAnalysis({
  isComparative,
  comparativeData,
  packId,
  testId,
  testIdTwo,
  isCombined = false,
}) {
  const [subjectMeta, setSubjectMeta] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [comparativeAttemptAnalysis, setComparativeAttemptAnalysis] = useState(null);
  const [userAttemptAnalysis, setUserAttemptAnalysis] = useState(null);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const res = await cachedGet(
          isCombined
            ? `/api/test/combined/${packId}/attempt-analysis?testIds=${testId},${testIdTwo}`
            : `/api/test/analysis/v2/${packId}/${testId}/attemptAnalysis`,
        );
        const result = res?.data?.data;
        const subjectMeta = result?.subjectMeta;
        setData(result);
        setSubjectMeta(subjectMeta);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }

    if (!isComparative) {
      fetchData();
    }
  }, []);

  useEffect(() => {
    if (!isComparative || !comparativeData) return undefined;
    setComparativeAttemptAnalysis(comparativeData?.comparativeData?.attempt_analysis);
    setUserAttemptAnalysis(comparativeData?.userData?.attempt_analysis);
    let subjectMeta = structuredClone(comparativeData?.subjectMeta);
    if (Object.keys(subjectMeta).length > 2) {
      const overall = { Overall: subjectMeta['Overall'] };
      delete subjectMeta['Overall'];
      subjectMeta = { ...overall, ...subjectMeta };
    } else {
      delete subjectMeta['Overall'];
    }
    setSubjectMeta(subjectMeta);
    setSelectedSubject(Object.values(subjectMeta)[0]);
  }, [isComparative, comparativeData]);

  const handleSubjectChange = (subject) => {
    setSelectedSubject(subject);
  };

  if (isLoading) return <TestAnalysisLoader />;

  if (isComparative && subjectMeta)
    return (
      <div className="space-y-8">
        <AttemptAnalysisLegend />
        <TableHeader headerTitle={'Tabular Analysis'} headerTitleClass={'border-b-0'}>
          <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
            <TabsContainer color={selectedSubject?.color}>
              {Object.values(subjectMeta)?.map((tab) => (
                <Tab
                  key={tab?._id}
                  isActive={selectedSubject?.title === tab?.title}
                  color={selectedSubject?.title === tab?.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                  tabColor={tab?.color}
                  bgColor="var(--q3-surface-dim)"
                  onClick={() => handleSubjectChange(tab)}
                >
                  {tab?.icon && <img src={tab?.icon} alt={tab?.title} className="" />}
                  <p className="mb-0">{tab?.title}</p>
                </Tab>
              ))}
            </TabsContainer>
          </div>
          <ComparativeAttemptAnalysisTable
            selectedSubject={selectedSubject}
            comparativeAttemptAnalysis={comparativeAttemptAnalysis}
            userAttemptAnalysis={userAttemptAnalysis}
            comparativeSegmentsMeta={comparativeData?.comparativeSegmentsMeta}
          />
        </TableHeader>
        <TableHeader headerTitle={'Graphical Analysis'} headerTitleClass={'border-b-0'}>
          <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
            <TabsContainer color={selectedSubject?.color}>
              {Object.values(subjectMeta)?.map((tab) => (
                <Tab
                  key={tab?._id}
                  isActive={selectedSubject?.title === tab?.title}
                  color={selectedSubject?.title === tab?.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                  tabColor={tab?.color}
                  bgColor="var(--q3-surface-dim)"
                  onClick={() => handleSubjectChange(tab)}
                >
                  {tab?.icon && <img src={tab?.icon} alt={tab?.title} className="" />}
                  <p className="mb-0">{tab?.title}</p>
                </Tab>
              ))}
            </TabsContainer>
          </div>
          <ComparativeAttemptAnalysisChart
            selectedSubject={selectedSubject}
            comparativeAttemptAnalysis={comparativeAttemptAnalysis}
            userAttemptAnalysis={userAttemptAnalysis}
            comparativeSegmentsMeta={comparativeData?.comparativeSegmentsMeta}
          />
        </TableHeader>
      </div>
    );

  return (
    <div>
      <AttemptAnalysisLegend />
      <div className="mt-4">
        <TableHeader headerTitle={'Summary'}>
          <AttemptAnalysisSummaryTable subjectMeta={subjectMeta} data={data} />
        </TableHeader>
      </div>
      <div className="mt-4">
        <TableHeader headerTitle="Graphical View of Attempts">
          <SubjectAttemptAnalysisChart data={data} subjectMeta={subjectMeta} />
        </TableHeader>
      </div>
    </div>
  );
}
